import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, TrendingDown, DollarSign, FileText, Users, 
  Clock, CheckCircle, AlertTriangle, Target, Award
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currency';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer
} from 'recharts';

const Analytics = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await apiClient.get('/analytics/complete');
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!analytics) return null;

  const { financial, projects, clients, team, change_requests, cash_flow } = analytics;

  // Chart colors
  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Project status data for pie chart
  const projectStatusData = [
    { name: 'Acquired', value: projects.projects_by_status.acquired, color: '#94a3b8' },
    { name: 'Ongoing', value: projects.projects_by_status.ongoing, color: '#3b82f6' },
    { name: 'Completed', value: projects.projects_by_status.completed, color: '#10b981' }
  ].filter(item => item.value > 0);

  // Cash flow chart data
  const cashFlowData = cash_flow.map(item => ({
    month: new Date(item.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    amount: item.amount
  }));

  // Top clients for bar chart
  const topClientsData = clients.top_clients.map(client => ({
    name: client.name.length > 15 ? client.name.substring(0, 15) + '...' : client.name,
    revenue: client.revenue
  }));

  return (
    <div className="space-y-8" data-testid="analytics-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Analytics & Insights</h1>
          <p className="text-slate-600 mt-1">Track your business performance and KPIs</p>
        </div>
        <Button onClick={fetchAnalytics} variant="outline">
          Refresh Data
        </Button>
      </div>

            {/* Alerts & Insights */}
      {(projects.delayed_projects_count > 0 || financial.overdue_count > 0 || change_requests.high_scope_creep_projects > 0) && (
        <Card className="border-rose-100 bg-rose-50">
          <CardHeader>
            <CardTitle className="text-rose-900">⚠️ Action Required</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {projects.delayed_projects_count > 0 && (
                <li className="flex items-start space-x-2">
                  <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5" />
                  <span className="text-sm text-rose-900">
                    <strong>{projects.delayed_projects_count}</strong> projects are delayed beyond expected completion date
                  </span>
                </li>
              )}
              {financial.overdue_count > 0 && (
                <li className="flex items-start space-x-2">
                  <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5" />
                  <span className="text-sm text-rose-900">
                    <strong>{financial.overdue_count}</strong> payments are overdue
                  </span>
                </li>
              )}
              {change_requests.high_scope_creep_projects > 0 && (
                <li className="flex items-start space-x-2">
                  <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5" />
                  <span className="text-sm text-rose-900">
                    <strong>{change_requests.high_scope_creep_projects}</strong> projects have high scope creep (5+ change requests)
                  </span>
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Financial KPIs */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Financial Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Income</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {getCurrencySymbol(user?.currency)}{financial.total_income.toFixed(0)}
                  </p>
                </div>
                <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Payments</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1">
                    {getCurrencySymbol(user?.currency)}{financial.pending_payments.toFixed(0)}
                  </p>
                </div>
                <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Income Realization</p>
                  <p className="text-2xl font-bold text-indigo-600 mt-1">
                    {financial.income_realization_rate.toFixed(1)}%
                  </p>
                </div>
                <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                  <Target className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg Project Value</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {getCurrencySymbol(user?.currency)}{financial.avg_project_value.toFixed(0)}
                  </p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Monthly Income Trend */}
      <Card className="border-slate-100">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Monthly Income Trend</CardTitle>
            <div className="flex items-center space-x-2">
              {financial.monthly_income.growth_percentage >= 0 ? (
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              ) : (
                <TrendingDown className="h-5 w-5 text-rose-600" />
              )}
              <span className={`text-sm font-medium ${
                financial.monthly_income.growth_percentage >= 0 ? 'text-emerald-600' : 'text-rose-600'
              }`}>
                {financial.monthly_income.growth_percentage >= 0 ? '+' : ''}
                {financial.monthly_income.growth_percentage.toFixed(1)}% MoM
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Current Month</p>
              <p className="text-lg font-bold text-slate-900">{getCurrencySymbol(user?.currency)}{financial.monthly_income.current_month.toFixed(0)}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Last Month</p>
              <p className="text-lg font-bold text-slate-900">{getCurrencySymbol(user?.currency)}{financial.monthly_income.last_month.toFixed(0)}</p>
            </div>
          </div>
          {cashFlowData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={cashFlowData}>
                <defs>
                  <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => `${getCurrencySymbol(user?.currency)}${value.toLocaleString()}`} />
                <Area type="monotone" dataKey="amount" stroke="#10b981" fillOpacity={1} fill="url(#colorAmount)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-slate-400">
              No cash flow data yet
            </div>
          )}
        </CardContent>
      </Card>


          {/* Additional KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-100">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
              <div>
                <p className="text-xs text-slate-500">Completion Rate</p>
                <p className="text-xl font-bold text-slate-900">{projects.completion_rate.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-10 w-10 text-rose-600" />
              <div>
                <p className="text-xs text-slate-500">Delayed Projects</p>
                <p className="text-xl font-bold text-rose-600">{projects.delayed_projects_count}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <Users className="h-10 w-10 text-indigo-600" />
              <div>
                <p className="text-xs text-slate-500">Team Members</p>
                <p className="text-xl font-bold text-slate-900">{team.total_team_members}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-100">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <Award className="h-10 w-10 text-amber-600" />
              <div>
                <p className="text-xs text-slate-500">Change Requests</p>
                <p className="text-xl font-bold text-slate-900">{change_requests.total_change_requests}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>




      {/* Project & Client Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Status Distribution */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle>Project Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <div className="text-center p-2 bg-slate-50 rounded">
                <p className="text-xs text-slate-500">Total</p>
                <p className="text-xl font-bold text-slate-900">{projects.total_projects}</p>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded">
                <p className="text-xs text-blue-600">Ongoing</p>
                <p className="text-xl font-bold text-blue-600">{projects.active_projects}</p>
              </div>
              <div className="text-center p-2 bg-emerald-50 rounded">
                <p className="text-xs text-emerald-600">Completed</p>
                <p className="text-xl font-bold text-emerald-600">{projects.completed_projects}</p>
              </div>
            </div>
            {projectStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={projectStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {projectStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-slate-400">
                No project data
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Clients by Revenue */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle>Top Clients by Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Total Clients</p>
                <p className="text-2xl font-bold text-slate-900">{clients.total_clients}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Repeat Rate</p>
                <p className="text-2xl font-bold text-indigo-600">{clients.repeat_client_rate.toFixed(0)}%</p>
              </div>
            </div>
            {topClientsData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={topClientsData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip formatter={(value) => `${getCurrencySymbol(user?.currency)}${value.toLocaleString()}`} />
                  <Bar dataKey="revenue" fill="#6366f1" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-slate-400">
                No client revenue data
              </div>
            )}
          </CardContent>
        </Card>
      </div>


    </div>
  );
};

export default Analytics;
